import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function PaymentsPage() {
    const [config, setConfig] = useState(null);
    const [clients, setClients] = useState([]);
    const [payments, setPayments] = useState([]);
    const [amount, setAmount] = useState("100.00");
    const [clientId, setClientId] = useState("");
    const [description, setDescription] = useState("Coaching session");
    const [err, setErr] = useState("");
    const [success, setSuccess] = useState("");

    const load = async () => {
        const [cfg, c, p] = await Promise.all([
            api.get("/payments/config"),
            api.get("/clients"),
            api.get("/payments"),
        ]);
        setConfig(cfg.data);
        setClients(c.data);
        setPayments(p.data);
    };
    useEffect(() => { load(); }, []);

    const refresh = async () => {
        const { data } = await api.get("/payments");
        setPayments(data);
    };

    const totals = {
        completed: payments.filter((p) => p.status === "COMPLETED").reduce((s, p) => s + p.amount, 0),
        pending: payments.filter((p) => p.status === "CREATED").length,
    };

    return (
        <div className="p-8 space-y-6" data-testid="payments-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="label-tiny mb-2">/ Billing</p>
                    <h1 className="font-serif text-6xl">Payments</h1>
                </div>
                <div className="text-right">
                    <p className="label-tiny">Lifetime</p>
                    <p className="font-display text-4xl text-[var(--success)]">${totals.completed.toFixed(2)}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Charge form */}
                <div className="lg:col-span-1 border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4" data-testid="charge-card">
                    <div>
                        <p className="label-tiny mb-1">/ New charge</p>
                        <h3 className="font-display text-2xl uppercase">Collect payment</h3>
                        <p className="text-xs text-[var(--text-muted)] mt-2 font-mono">{config?.env?.toUpperCase()} · USD</p>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Client</label>
                        <select className="input-dark" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="charge-client-select">
                            <option value="">— optional —</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Amount (USD)</label>
                        <input className="input-dark" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="charge-amount-input" />
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Description</label>
                        <input className="input-dark" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>

                    {err && <div className="text-sm text-[var(--danger)]" data-testid="charge-error">{err}</div>}
                    {success && <div className="text-sm text-[var(--success)]" data-testid="charge-success">{success}</div>}

                    {config?.client_id ? (
                        <div className="pt-2" data-testid="paypal-button-wrap">
                            <PayPalScriptProvider
                                options={{ clientId: config.client_id, currency: "USD", intent: "capture" }}
                            >
                                <PayPalButtons
                                    style={{ layout: "vertical", color: "black", label: "pay", shape: "rect" }}
                                    forceReRender={[amount, clientId, description]}
                                    createOrder={async () => {
                                        setErr(""); setSuccess("");
                                        try {
                                            const { data } = await api.post("/payments/create-order", {
                                                amount: parseFloat(amount),
                                                client_id: clientId || null,
                                                description,
                                            });
                                            return data.order_id;
                                        } catch (e) {
                                            setErr(formatApiError(e.response?.data?.detail) || e.message);
                                            throw e;
                                        }
                                    }}
                                    onApprove={async (data) => {
                                        try {
                                            const res = await api.post(`/payments/capture-order/${data.orderID}`);
                                            setSuccess(`Payment ${res.data.status} – $${amount}`);
                                            refresh();
                                        } catch (e) {
                                            setErr(formatApiError(e.response?.data?.detail) || e.message);
                                        }
                                    }}
                                    onError={(e) => setErr(e?.message || "PayPal error")}
                                />
                            </PayPalScriptProvider>
                        </div>
                    ) : (
                        <div className="text-xs text-[var(--text-muted)] border border-dashed border-[var(--border-bright)] p-3">
                            PayPal not configured. Add PAYPAL_CLIENT_ID to .env.
                        </div>
                    )}
                </div>

                {/* History */}
                <div className="lg:col-span-2 border border-[var(--border)] bg-[var(--surface)]" data-testid="payments-history">
                    <div className="p-5 border-b border-[var(--border)]">
                        <p className="label-tiny mb-1">/ History</p>
                        <h3 className="font-display text-2xl uppercase">All transactions</h3>
                    </div>
                    <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[var(--border)] label-tiny">
                        <div className="col-span-3">Date</div>
                        <div className="col-span-3">Client</div>
                        <div className="col-span-3">Description</div>
                        <div className="col-span-1 text-right">Amount</div>
                        <div className="col-span-2 text-right">Status</div>
                    </div>
                    {payments.length === 0 ? (
                        <p className="p-8 text-center text-[var(--text-muted)] text-sm">No payments yet — collect your first one →</p>
                    ) : payments.map((p) => (
                        <div key={p.id} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[var(--border)] text-sm items-center" data-testid={`payment-row-${p.id}`}>
                            <div className="col-span-3 font-mono text-xs">{new Date(p.created_at).toLocaleString()}</div>
                            <div className="col-span-3">{p.client_name || "—"}</div>
                            <div className="col-span-3 text-[var(--text-muted)] truncate">{p.description || "—"}</div>
                            <div className="col-span-1 text-right">${p.amount.toFixed(2)}</div>
                            <div className="col-span-2 text-right">
                                <span className={`tag-pill ${
                                    p.status === "COMPLETED" ? "border-[var(--success)] text-[var(--success)]" :
                                    p.status === "FAILED" ? "border-[var(--danger)] text-[var(--danger)]" :
                                    "border-[var(--warning)] text-[var(--warning)]"
                                }`}>{p.status}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
